import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config';
import { Link } from 'react-router-dom';
import { bookList, workshops } from '../data/homeData';
import Header from '../components/Header';
import SEO from '../components/SEO';
import './Home.css';
import bgImage from '../assets/images/bg/919e6e25a043681718250974ce51cf16.jpeg';

// 添加懒加载图片组件
const LazyImage = ({ src, alt, className, width = "300", height = "200", onError }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${isLoaded ? 'loaded' : ''}`}
      width={width}
      height={height}
      loading="lazy"
      onLoad={() => setIsLoaded(true)}
      onError={onError}
    />
  );
};

const Home = () => {
  const [booksWithPrivacy, setBooksWithPrivacy] = useState([]);
  const [eventsWithPrivacy, setEventsWithPrivacy] = useState([]);
  const [latestNews, setLatestNews] = useState([]);

  // 构建首页的结构化数据
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "养花怪兽儿童阅读馆 - 首页",
    "description": "专业的儿童绘本推荐平台，提供绘本分类导航、热门推荐、新书速递等服务。",
    "publisher": {
      "@type": "Organization",
      "name": "养花怪兽儿童阅读馆",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "offers": {
      "@type": "AggregateOffer",
      "offerCount": "7000+",
      "itemOffered": {
        "@type": "Book",
        "bookFormat": "http://schema.org/Paperback",
        "category": "儿童绘本"
      }
    }
  };

  useEffect(() => {
    // 设置背景图片
    document.documentElement.style.setProperty('--welcome-bg', `url(${bgImage})`);
    return () => {
      document.documentElement.style.setProperty('--welcome-bg', 'none');
    };
  }, []);

  const getPrivacyLink = async (imageUrl) => {
    try {
      const response = await fetch(`${BASE_URL}/privacy_link?img_url=${encodeURIComponent(imageUrl)}`);
      const data = await response.json();
      if (typeof data === 'string') {
        return data;
      }
      return data.link || data;
    } catch (error) {
      console.error('Error fetching privacy link:', error);
      return imageUrl;
    }
  };

  const fetchLatestNews = async () => {
    try {
      const response = await fetch(`${BASE_URL}/news`);
      const data = await response.json();
      setLatestNews(data);
    } catch (error) {
      console.error('Error fetching latest news:', error);
    }
  };

  useEffect(() => {
    const processData = async () => {
      const processedBooks = await Promise.all(
        bookList.map(async (book) => {
          const privacyLink = await getPrivacyLink(book.image);
          return {
            ...book,
            privacyImage: privacyLink
          };
        })
      );
      setBooksWithPrivacy(processedBooks);

      const processedEvents = await Promise.all(
        workshops.map(async (event) => {
          const privacyLink = await getPrivacyLink(event.image);
          return {
            ...event,
            privacyImage: privacyLink
          };
        })
      );
      setEventsWithPrivacy(processedEvents);

      await fetchLatestNews();
    };

    processData();
  }, []);

  return (
    <div className="page-container">
      <SEO 
        page="home"
        structuredData={homeStructuredData}
      />
      <Header />
      <main className="main-content full-width" itemScope itemType="https://schema.org/WebPage">
        <section className="welcome-section with-bg">
          <div className="welcome-content">
            <h1 itemProp="headline" style={{ color: '#f2f2f2' }}>欢迎来到养花怪兽</h1>
            <p itemProp="description">探索精彩绘本世界，参与丰富活动</p>
          </div>
        </section>

        <section className="featured-books" itemScope itemType="https://schema.org/CollectionPage">
          <h2 itemProp="name">最新推荐</h2>
          <div className="book-grid">
            {booksWithPrivacy.map(book => (
              <div key={book.id} className="book-card" itemScope itemType="https://schema.org/Book">
                <div className="book-cover-container">
                  <LazyImage
                    src={book.privacyImage || '/images/default-img-cover.png'}
                    alt={`${book.title}封面`}
                    className="book-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/default-img-cover.png';
                    }}
                  />
                </div>
                <div className="book-info">
                  <h3 itemProp="name">{book.title}</h3>
                  <p itemProp="author">{book.author}</p>
                  <div className="book-rating" itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
                    <meta itemProp="ratingValue" content={book.score} />
                    <meta itemProp="bestRating" content="5" />
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span
                        key={index}
                        className={`star ${index < Math.floor(book.score) ? 'filled' : ''}`}
                      >
                        ★
                      </span>
                    ))}
                    <span className="rating-value">({book.score})</span>
                  </div>
                  {book.description && <p itemProp="description" className="description">{book.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="upcoming-events">
          <h3>热门活动</h3>
          <div className="event-list">
            {eventsWithPrivacy.map(event => (
              <div key={event.id} className="event-card">
                <div className="event-status">报名中</div>
                <LazyImage
                  src={event.privacyImage || '/images/default-img-cover.png'}
                  alt={event.title}
                  className="event-cover"
                  width="300"
                  height="200"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/default-img-cover.png';
                  }}
                />
                <div className="event-card-content">
                  <h4>{event.title}</h4>
                  <div className="event-meta">
                    <span className="event-date">{event.date}</span>
                    <span className="event-location">{event.location}</span>
                  </div>
                  <p>{event.description}</p>
                  <div className="event-footer">
                    <Link to="/registration" className="register-btn">
                      立即报名
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="featured-services">
          <h3>我们的特色服务</h3>
          <div className="service-grid">
            <div className="service-card">
              <div className="icon">📚</div>
              <h4>绘本借阅</h4>
              <p>海量精选绘本，随时借阅</p>
            </div>
            <div className="service-card">
              <div className="icon">🎨</div>
              <h4>创意工作坊</h4>
              <p>激发孩子创造力</p>
            </div>
            <div className="service-card">
              <div className="icon">👨‍🏫</div>
              <h4>专家指导</h4>
              <p>专业阅读指导</p>
            </div>
            <div className="service-card">
              <div className="icon">🏕️</div>
              <h4>阅读营</h4>
              <p>沉浸式阅读体验</p>
            </div>
            <div className="service-card">
              <div className="icon">🌿</div>
              <h4>丰富的自然探索</h4>
              <p>户外阅读与自然教育</p>
            </div>
            <div className="service-card">
              <div className="icon">🎲</div>
              <h4>益智桌游</h4>
              <p>寓教于乐的互动体验</p>
            </div>
            <div className="service-card">
              <div className="icon">🏮</div>
              <h4>传统文化体验</h4>
              <p>传统节日与民俗文化</p>
            </div>
            <div className="service-card">
              <div className="icon">🖋️</div>
              <h4>书法启蒙</h4>
              <p>感受汉字之美</p>
            </div>
            <div className="service-card">
              <div className="icon">🎭</div>
              <h4>皮影戏工作坊</h4>
              <p>传统艺术体验</p>
            </div>
            <div className="service-card">
              <div className="icon">🎋</div>
              <h4>二十四节气课堂</h4>
              <p>感受自然与文化的智慧</p>
            </div>
            <div className="service-card">
              <div className="icon">🏯</div>
              <h4>古建筑探秘</h4>
              <p>了解传统建筑文化</p>
            </div>
            <div className="service-card">
              <div className="icon">🎎</div>
              <h4>传统服饰体验</h4>
              <p>感受服饰文化魅力</p>
            </div>
          </div>
        </section>

        <section className="statistics-section">
          <h3>我们的成就</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <h4>50+</h4>
              <p>会员用户</p>
            </div>
            <div className="stat-card">
              <h4>7000+</h4>
              <p>精选绘本</p>
            </div>
            <div className="stat-card">
              <h4>10+</h4>
              <p>成功活动</p>
            </div>
          </div>
        </section>

        <section className="testimonials-section">
          <h3>用户评价</h3>
          <div className="testimonial-carousel">
            <div className="testimonial-card">
              <p className="quote">"这里的绘本质量很高，孩子非常喜欢！"</p>
              <p className="author">- 张妈妈</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"活动很有创意，工作人员很专业。"</p>
              <p className="author">- 李先生</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"每周的绘本推荐都很贴心，省去了我们挑选的时间。"</p>
              <p className="author">- 王女士</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"孩子在这里养成了良好的阅读习惯，真的很感谢！"</p>
              <p className="author">- 刘爸爸</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"环境温馨舒适，孩子每次来都很开心。"</p>
              <p className="author">- 陈妈妈</p>
            </div>
          </div>
        </section>

        <section className="latest-news">
          <h3>最新动态</h3>
          <div className="news-list">
            {latestNews.map(news => (
              <div key={news.id} className="news-card">
                <h4>{news.title}</h4>
                <p>{news.content}</p>
                <div className="news-footer">
                  {news.url && (
                    <a 
                      href={news.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="news-link"
                    >
                      阅读更多
                    </a>
                  )}
                  <p className="date">{news.date}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
